import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col } from "react-bootstrap";
import NavBar from "../Components/NavBar/NavBar";
import Footer from "../Components/Footer/Footer";

const tocItems = [
    { id: "information", label: "1. What Personal Information We Collect" },
    { id: "usage", label: "2. What We Do With the Personal Information We Collect" },
    { id: "disclosure", label: "3. When We Disclose Personal Information" },
    { id: "cookies", label: "4. How We Use Cookies and Tech" },
    { id: "security", label: "5. Security" },
    { id: "transfers", label: "6. Transfers to Other Countries" },
    { id: "links", label: "7. Links to Other Sites" },
    { id: "choices", label: "8. Your Choices" },
    { id: "access", label: "9. Access & Corrections" },
    { id: "children", label: "10. Children" },
    { id: "contact", label: "11. Contact Us" },
];

const sectionContent = {
    information: `
We collect information such as name, email, phone number, address, billing details, and government ID (e.g. NRIC, FIN, passport) when you register, apply for EMI plans, or communicate with us. Technical data like IP address, browser type, and device identifiers may also be collected automatically.
  `,

    usage: `
We use your information to process transactions, verify identity, deliver customer support, personalize your experience, and improve our services. For EMI users, we may also evaluate eligibility based on financial data and usage behavior.
  `,

    disclosure: `
We do not sell your personal data. We may disclose it to trusted partners (e.g. DBS Bank, verification providers) as required to offer EMI services, comply with legal obligations, or protect our rights. All partners adhere to strict confidentiality standards.
  `,

    cookies: `
We use cookies and similar technologies to remember preferences, enhance security, track website usage, and serve tailored content. You can manage your cookie preferences via your browser or opt-out from non-essential cookies.
  `,

    security: `
We implement administrative, technical, and physical safeguards to protect your personal data. This includes encryption, access controls, and regular system audits. However, no online system is 100% secure, and you share data at your own risk.
  `,

    transfers: `
As a global business, your data may be processed in countries outside Singapore. We ensure appropriate safeguards such as standard contractual clauses or adequacy decisions are in place to comply with international privacy laws.
  `,

    links: `
Our site may link to third-party platforms (e.g. banks, support portals) which have their own privacy practices. We are not responsible for their content or data policies. Please review their privacy terms before engaging.
  `,

    choices: `
You have the right to opt out of marketing emails, restrict data collection, or deactivate your account. These can be done via your account settings or by contacting our support team.
  `,

    access: `
You may request to access, correct, or delete your personal data at any time. We aim to respond within 14 days. Proof of identity may be required for security reasons.
  `,

    children: `
Our services are not intended for children under 13. We do not knowingly collect personal data from minors. If you believe a child has provided us data, please contact us to have it removed.
  `,

    contact: `
If you have any questions or concerns about this Privacy Policy, please reach out to us at:

**AZZ Enterprises Pte Ltd**  
Email: privacy@azz.com.sg  
Phone: +65 1234 5678
  `,
};

const PrivacyPolicy = () => {
    const [activeId, setActiveId] = useState("");

    useEffect(() => {
        const handleScroll = () => {
            const offset = 100;
            for (let i = tocItems.length - 1; i >= 0; i--) {
                const section = document.getElementById(tocItems[i].id);
                if (section && window.scrollY + offset >= section.offsetTop) {
                    setActiveId(tocItems[i].id);
                    break;
                }
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="d-flex flex-column min-vh-100 bg-white">
            <Helmet>
                <title>Privacy Policy – AZZ Enterprises</title>
            </Helmet>

            <NavBar />

            <Container fluid className="py-5 px-3 px-lg-5" style={{ maxWidth: "1400px" }}>
                <Row>
                    <Col lg={8}>
                        <h1 className="fw-bold display-4 mb-2">Privacy Policy</h1>
                        <p className="text-muted mb-5">Last updated July 1st, 2025</p>

                        <p className="lead text-dark mb-4">
                            This Privacy Policy helps you understand how AZZ Enterprises collects, uses, and safeguards your information when you use our services.
                        </p>

                        {tocItems.map(({ id, label }) => (
                            <section className="mb-4" id={id} key={id}>
                                <h5 className="fw-bold mb-2">{label}</h5>
                                <p className="text-muted" style={{ whiteSpace: "pre-line" }}>
                                    {sectionContent[id]}
                                </p>
                            </section>
                        ))}
                    </Col>

                    <Col lg={4} className="d-none d-lg-block">
                        <div className="sticky-top pt-2" style={{ top: "100px" }}>
                            <h6 className="text-dark mb-3 fw-bold">Table of contents</h6>
                            <ul className="list-unstyled small">
                                {tocItems.map(({ id, label }) => (
                                    <li className="mb-2" key={id}>
                                        <a
                                            href={`#${id}`}
                                            className={`text-decoration-none ${activeId === id ? "text-primary fw-semibold" : "text-muted"
                                                }`}
                                        >
                                            {label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <a href="#top" className="text-muted small d-block mt-3">
                                ⬆ Back to top
                            </a>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
